import {
  InsightConflict,
  InsightDecision,
  KeyMoment,
  MeetingInsights,
  SlideTimelineEntry,
  TaggedReplica,
} from '../../../services/api.service';

export type MomentMarkerType = 'KPI' | 'dispute' | 'decision' | 'risk' | 'task';

export type VideoMoment = {
  seconds: number;
  source_ts: number;
  type: MomentMarkerType;
  label: string;
  speaker?: string;
  slideIndex?: number;
};

export const MARKER_COLORS: Record<MomentMarkerType, string> = {
  dispute: '#EF4444',
  KPI: '#3B82F6',
  decision: '#10B981',
  risk: '#F59E0B',
  task: '#F59E0B',
};

export const MARKER_LABELS: Record<MomentMarkerType, string> = {
  dispute: 'спор',
  KPI: 'KPI',
  decision: 'решение',
  risk: 'риск',
  task: 'задача',
};

const TAG_TO_MARKER: Record<string, MomentMarkerType> = {
  CONFLICT: 'dispute',
  DISPUTE: 'dispute',
  KPI: 'KPI',
  DECISION: 'decision',
  TASK: 'task',
  RISK: 'risk',
};

function truncate(text: string, max = 72): string {
  const t = text.trim().replace(/\s+/g, ' ');
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

function tagTypeToMarker(tagType?: string, tag?: string): MomentMarkerType | null {
  const tt = (tagType || '').toLowerCase();
  const tg = (tag || '').toLowerCase();
  if (tt === 'dispute' || tt === 'conflict' || tg === 'спор' || tg === 'конфликт') return 'dispute';
  if (tt === 'decision' || tg === 'решение') return 'decision';
  if (tt === 'task' || tg === 'задача') return 'task';
  if (tg === 'риск' || tg === 'risk') return 'risk';
  if (tt === 'action' || tg === 'kpi') return 'KPI';
  return null;
}

function pushMoment(
  list: VideoMoment[],
  seconds: number,
  type: MomentMarkerType,
  label: string,
  speaker?: string,
) {
  if (!Number.isFinite(seconds) || seconds < 0 || !label.trim()) return;
  list.push({
    seconds,
    source_ts: seconds,
    type,
    label: truncate(label),
    speaker,
  });
}

export function buildVideoMoments(insights: MeetingInsights | null | undefined): VideoMoment[] {
  if (!insights) return [];
  const raw: VideoMoment[] = [];

  insights.decisions?.forEach((d: InsightDecision) => {
    pushMoment(raw, d.time, 'decision', d.text, d.speaker);
  });

  insights.conflicts?.forEach((c: InsightConflict) => {
    const type = c.tag_type === 'dispute' || c.tag === 'dispute' ? 'dispute' : 'dispute';
    pushMoment(raw, c.time, type, c.text, c.speakers?.[0]);
  });

  insights.key_moments?.forEach((m: KeyMoment) => {
    const type = tagTypeToMarker(m.tag_type, m.tag);
    if (type) pushMoment(raw, m.time, type, m.text);
  });

  insights.tagged_replicas?.forEach((tr: TaggedReplica) => {
    const sec = tr.timestamp_sec ?? 0;
    const text = tr.translated_text || tr.text;
    tr.tags.forEach(tag => {
      const mapped = TAG_TO_MARKER[tag.toUpperCase()];
      if (mapped) pushMoment(raw, sec, mapped, text, tr.speaker);
    });
  });

  insights.kpi_table?.forEach(row => {
    if (!row.metric) return;
    const label = [row.metric, row.fact != null ? `факт ${row.fact}` : '', row.plan != null ? `план ${row.plan}` : '']
      .filter(Boolean)
      .join(' · ');
    const sec = parseKpiTimestamp(row.timestamp);
    if (sec != null) pushMoment(raw, sec, 'KPI', label);
  });

  return dedupeMoments(raw);
}

function parseKpiTimestamp(ts?: string): number | null {
  if (!ts) return null;
  const parts = ts.split(':').map(Number);
  if (parts.some(n => Number.isNaN(n))) return null;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return null;
}

function dedupeMoments(moments: VideoMoment[]): VideoMoment[] {
  const priority: Record<MomentMarkerType, number> = {
    dispute: 5, risk: 4, decision: 3, task: 2, KPI: 1,
  };
  const sorted = [...moments].sort((a, b) => a.seconds - b.seconds);
  const out: VideoMoment[] = [];

  for (const m of sorted) {
    const dup = out.find(x => Math.abs(x.seconds - m.seconds) < 2 && x.type === m.type);
    if (dup) {
      if (m.label.length > dup.label.length) dup.label = m.label;
      continue;
    }
    const near = out.find(x => Math.abs(x.seconds - m.seconds) < 2);
    if (near && priority[m.type] > priority[near.type]) {
      Object.assign(near, m);
      continue;
    }
    if (!near) out.push({ ...m });
  }

  return out.sort((a, b) => a.seconds - b.seconds);
}

export function attachSlideThumbnails(
  moments: VideoMoment[],
  slideTimeline: SlideTimelineEntry[],
): VideoMoment[] {
  if (!slideTimeline.length) return moments;
  const slides = [...slideTimeline]
    .filter(s => s.slide_index != null)
    .sort((a, b) => (a.time_sec ?? 0) - (b.time_sec ?? 0));

  return moments.map(m => {
    let best = slides[0];
    let bestDelta = Math.abs((best.time_sec ?? 0) - m.seconds);
    for (const s of slides) {
      const delta = Math.abs((s.time_sec ?? 0) - m.seconds);
      if (delta < bestDelta) {
        best = s;
        bestDelta = delta;
      }
    }
    if (bestDelta <= 45 && best.slide_index != null) {
      return { ...m, slideIndex: best.slide_index };
    }
    return m;
  });
}

export function segmentMatchesMomentFilter(tags: string[], filter: MomentMarkerType | 'all'): boolean {
  if (filter === 'all') return true;
  const upper = tags.map(t => t.toUpperCase());
  if (filter === 'dispute') return upper.some(t => t === 'CONFLICT' || t === 'DISPUTE');
  if (filter === 'KPI') return upper.includes('KPI');
  if (filter === 'decision') return upper.includes('DECISION');
  if (filter === 'risk') return upper.includes('RISK');
  if (filter === 'task') return upper.includes('TASK');
  return true;
}
